import { AxiosResponse } from 'axios';
import { SET_JOB_APPLICATIONS, SET_JOB_APPLICATION, SET_JOB_APPLICATION_STATS, SET_ERRORMSG } from '@/store/mutation_types';
import {
  CLEAR_JOB_APPLICATION_ISLOADING,
  DELETE_JOB_APPLICATION,
  EDIT_JOB_APPLICATION,
  GET_JOB_APPLICATIONS,
  GET_JOB_APPLICATION,
  REGISTER_JOB_APPLICATION,
  UPDATE_JOB_APPLICATION_STATUS,
  GET_JOB_APPLICATION_STATS,
} from '@/store/action_types';
import { StateObject } from '@/dtos/state.dto';
import JobApplication from '@/services/job_application';
import { Module } from 'vuex';
import { GetJobApplication, GetJobApplicationStats } from '@/dtos/job_application';

const jobApplication: Module<any, {}> = {
  state: {
    jobApplication: {
      data: null,
      isLoading: false,
    },
    jobApplications: {
      data: null,
      metaData: null,
      isLoading: false,
    },
    jobApplicationStats: {
      data: null,
      isLoading: false,
    },
  },
  getters: {
    jobApplication: (state: { jobApplication: any }) => state.jobApplication,
    jobApplications: (state: { jobApplications: any }) => state.jobApplications,
    jobApplicationStats: (state: { jobApplicationStats: any }) => state.jobApplicationStats,
  },
  mutations: {
    [SET_JOB_APPLICATION]: (
      state: { jobApplication: StateObject },
      payload: { data?: any; status?: any; isLoading: any }
    ) => {
      if (payload.data !== undefined) state.jobApplication.data = payload.data;
      state.jobApplication.isLoading = payload.isLoading;
    },
    [SET_JOB_APPLICATIONS]: (
      state: { jobApplications: StateObject },
      payload: { data?: any; metaData?: any; isLoading: any }
    ) => {
      if (payload.data !== undefined) state.jobApplications.data = payload.data;
      if (payload.metaData !== undefined) state.jobApplications.metaData = payload.metaData;
      state.jobApplications.isLoading = payload.isLoading;
    },
    [SET_JOB_APPLICATION_STATS]: (
      state: { jobApplicationStats: StateObject },
      payload: { data?: any; isLoading: any }
    ) => {
      if (payload.data !== undefined) state.jobApplicationStats.data = payload.data;
      state.jobApplicationStats.isLoading = payload.isLoading;
    },
  },
  actions: {
    [REGISTER_JOB_APPLICATION]: async ({ commit }: any, payload: any) => {
      commit(SET_JOB_APPLICATION, { data: null, isLoading: true });

      const result: any = await JobApplication.registerJobApplication(payload);

      commit(SET_JOB_APPLICATION, { data: result?.ok ? result.data : null, isLoading: false });
      return result;
    },
    [GET_JOB_APPLICATIONS]: async ({ commit }: any, payload: GetJobApplication) => {
      commit(SET_JOB_APPLICATIONS, { data: null, isLoading: true });

      const result: AxiosResponse = await JobApplication.getJobApplications(payload);

      if (result) {
        if (result.data.status) {
          commit(SET_JOB_APPLICATIONS, {
            data: result.data.data,
            metaData: result.data.metaData,
            isLoading: false,
          });
          return true;
        }
        commit(SET_JOB_APPLICATIONS, { data: null, isLoading: false });
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      }
      commit(SET_JOB_APPLICATIONS, { isLoading: false });
      return false;
    },
    [GET_JOB_APPLICATION]: async ({ commit }: any, payload: GetJobApplication) => {
      commit(SET_JOB_APPLICATION, { data: null, isLoading: true });

      const result: AxiosResponse = await JobApplication.getJobApplications(payload);

      if (result) {
        if (result.data.status) {
          commit(SET_JOB_APPLICATION, { data: result.data.data[0] ?? null, isLoading: false });
          return true;
        }
        commit(SET_JOB_APPLICATION, { data: null, isLoading: false });
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      }
      commit(SET_JOB_APPLICATION, { isLoading: false });
      return false;
    },
    [EDIT_JOB_APPLICATION]: async ({ commit }: any, payload: any) => {
      commit(SET_JOB_APPLICATION, { data: null, isLoading: true });

      const result: any = await JobApplication.editJobApplication(payload);

      commit(SET_JOB_APPLICATION, { data: result?.ok ? result.data : null, isLoading: false });
      return result;
    },
    [UPDATE_JOB_APPLICATION_STATUS]: async ({ commit, state }: any, payload: { id: string; status: string }) => {
      const result: AxiosResponse = await JobApplication.updateJobApplicationStatus(payload);

      if (result) {
        if (result.data.status) {
          if (state.jobApplication.data && state.jobApplication.data.id === payload.id) {
            commit(SET_JOB_APPLICATION, { data: { ...state.jobApplication.data, status: payload.status }, isLoading: false });
          }
          if (Array.isArray(state.jobApplications.data)) {
            commit(SET_JOB_APPLICATIONS, {
              data: state.jobApplications.data.map((a: any) => (a.id === payload.id ? { ...a, status: payload.status } : a)),
              isLoading: state.jobApplications.isLoading,
            });
          }
          return true;
        }
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      }
      return false;
    },
    [DELETE_JOB_APPLICATION]: async ({ commit, state }: any, payload: string) => {
      commit(SET_JOB_APPLICATION, { isLoading: true });

      const result: AxiosResponse = await JobApplication.deleteJobApplication(payload);

      if (result) {
        if (result.data.status) {
          commit(SET_JOB_APPLICATION, { data: null, isLoading: false });
          if (Array.isArray(state.jobApplications.data)) {
            const meta = state.jobApplications.metaData;
            commit(SET_JOB_APPLICATIONS, {
              data: state.jobApplications.data.filter((a: any) => a.id !== payload),
              metaData: meta ? { ...meta, totalRecords: Math.max(0, (meta.totalRecords ?? 1) - 1) } : meta,
              isLoading: state.jobApplications.isLoading,
            });
          }
          return true;
        }
        commit(SET_JOB_APPLICATION, { isLoading: false });
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      }
      commit(SET_JOB_APPLICATION, { isLoading: false });
      return false;
    },
    [GET_JOB_APPLICATION_STATS]: async ({ commit }: any, payload: GetJobApplicationStats) => {
      commit(SET_JOB_APPLICATION_STATS, { isLoading: true });

      const result: AxiosResponse = await JobApplication.getJobApplicationStats(payload);

      if (result) {
        if (result.data.status) {
          commit(SET_JOB_APPLICATION_STATS, { data: result.data.data, isLoading: false });
          return result.data.data;
        }
        commit(SET_JOB_APPLICATION_STATS, { data: null, isLoading: false });
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      }
      commit(SET_JOB_APPLICATION_STATS, { isLoading: false });
      return false;
    },
    [CLEAR_JOB_APPLICATION_ISLOADING]: ({ commit }) => {
      commit(SET_JOB_APPLICATION, { isLoading: false });
      commit(SET_JOB_APPLICATIONS, { isLoading: false });
    },
  },
};

export default jobApplication;
